import { deviceApi } from "#/api/deviceApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import BaseTable, { type TableSortProps } from "#/components/tables/BaseTable";
import { formatDate, formatTime } from "#/utils/dateTimeUtils";
import { getHardwareLabel } from "#/utils/deviceHardware";
import { Badge, Table } from "@mantine/core";
import { IconEye } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface ClientDevicesTableProps {
  clientId: number;
}

const headers = [
  { key: "name", value: "Name" },
  { key: "is_active", value: "Active" },
  { key: "hardware_type", value: "Hardware" },
  { key: "site", value: "Site" },
  { key: "last_seen", value: "Last Seen" },
  { key: "actions", value: "" },
];

export default function ClientDevicesTable({ clientId }: ClientDevicesTableProps) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sortBy, setSortBy] = useState("name");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("ASC");

  const { data: { devices = [], total = 0 } = {}, isPending } = useQuery({
    queryKey: ["client-devices", clientId, page, limit, sortBy, sortDirection],
    queryFn: async () => {
      const response = await deviceApi.getAll({
        page,
        limit,
        sortBy,
        order: sortDirection,
        filterBy: `client_id=${clientId}`,
      });
      return {
        devices: response.data.data,
        total: response.data.total,
      };
    },
  });

  return (
    <div className="flex flex-col gap-2 h-full">
      <BaseTable
        headers={headers.map((h) => h.value)}
        loading={isPending}
        allowSorting
        sortableColumns={[0, 1, 2, 3, 4]}
        onSortChange={(e: TableSortProps) => {
          setPage(1);
          setSortDirection(e.direction);
          setSortBy(headers[e.index].key);
        }}
        rows={devices.map((device) => (
          <Table.Tr key={device.id}>
            <Table.Td>{device.name}</Table.Td>
            <Table.Td>
              <Badge color={device.is_active ? "green" : "red"} size="sm">
                {device.is_active ? "Yes" : "No"}
              </Badge>
            </Table.Td>
            <Table.Td className="capitalize">
              {getHardwareLabel(device.hardware_type, "—")}
            </Table.Td>
            <Table.Td>{device.site?.name ?? "—"}</Table.Td>
            <Table.Td>
              {device.last_seen ? (
                <>
                  {formatDate(device.last_seen)}
                  <br />
                  <span className="text-xs text-gray-500">
                    {formatTime(device.last_seen)}
                  </span>
                </>
              ) : (
                "—"
              )}
            </Table.Td>
            <Table.Td>
              <ActionButton
                icon={IconEye}
                onClick={() => navigate(`/clients/${clientId}/devices/${device.id}`)}
              />
            </Table.Td>
          </Table.Tr>
        ))}
      />
      <Pagination
        page={page}
        limit={limit}
        total={total}
        onChange={setPage}
        onLimitChange={setLimit}
      />
    </div>
  );
}
